import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  Copy,
  ExternalLink,
  Search,
  FileText,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  LayoutTemplate
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPagesPage = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPage, setNewPage] = useState({ title: "", slug: "" });
  const [creating, setCreating] = useState(false);

  // Fetch pages
  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/pages`);
      setPages(res.data);
    } catch (error) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  // Initialize system pages
  const initializeSystemPages = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/pages/initialize-system-pages`);
      toast.success(`Initialized ${res.data.initialized.length} pages`);
      fetchPages();
    } catch (error) {
      toast.error("Failed to initialize pages");
    }
  };

  // Create new page
  const createPage = async () => {
    if (!newPage.title || !newPage.slug) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/admin/pages`, {
        title: newPage.title,
        slug: newPage.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        is_published: true,
        is_system_page: false,
        sections: []
      });
      toast.success("Page created!");
      setShowCreateModal(false);
      setNewPage({ title: "", slug: "" });
      navigate(`/admin/pages/${res.data.page.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create page");
    } finally {
      setCreating(false);
    }
  };

  // Duplicate page
  const duplicatePage = async (pageId) => {
    try {
      const res = await axios.post(`${API}/api/admin/pages/${pageId}/duplicate`);
      toast.success("Page duplicated!");
      fetchPages();
    } catch (error) {
      toast.error("Failed to duplicate page");
    }
  };

  // Delete page
  const deletePage = async (page) => {
    if (page.is_system_page) {
      toast.error("Cannot delete system pages");
      return;
    }
    
    if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/api/admin/pages/${page.id}`);
      toast.success("Page deleted");
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete page");
    }
  };

  // Toggle publish status
  const togglePublish = async (page) => {
    try {
      await axios.put(`${API}/api/admin/pages/${page.id}`, {
        is_published: !page.is_published
      });
      toast.success(page.is_published ? "Page unpublished" : "Page published");
      fetchPages();
    } catch (error) {
      toast.error("Failed to update page");
    }
  };

  // Filter pages
  const filteredPages = pages.filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate system and custom pages
  const systemPages = filteredPages.filter(p => p.is_system_page);
  const customPages = filteredPages.filter(p => !p.is_system_page);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Page Builder
            </h1>
            <p className="text-[#A1A1AA] mt-1">Create and manage your website pages</p>
          </div>
          
          <div className="flex gap-3">
            {pages.length === 0 && (
              <button
                onClick={initializeSystemPages}
                className="flex items-center gap-2 px-4 py-2 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
              >
                <LayoutTemplate className="w-4 h-4" />
                Initialize Default Pages
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Page
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
          <input
            type="text"
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] pl-12 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none"
          />
        </div>

        {/* System Pages Section */}
        {systemPages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
              <Lock className="w-4 h-4" /> System Pages
            </h2>
            <div className="grid gap-4">
              {systemPages.map((page) => (
                <PageCard
                  key={page.id}
                  page={page}
                  onEdit={() => navigate(`/admin/pages/${page.id}/edit`)}
                  onDuplicate={() => duplicatePage(page.id)}
                  onDelete={() => deletePage(page)}
                  onTogglePublish={() => togglePublish(page)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Custom Pages Section */}
        <div>
          <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Custom Pages ({customPages.length})
          </h2>
          
          {customPages.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[rgba(212,175,55,0.2)]">
              <FileText className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
              <h3 className="text-[#F5F5F0] font-semibold mb-2">No custom pages yet</h3>
              <p className="text-[#A1A1AA] mb-4">Create your first custom page to get started</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors"
              >
                Create Page
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {customPages.map((page) => (
                <PageCard
                  key={page.id}
                  page={page}
                  onEdit={() => navigate(`/admin/pages/${page.id}/edit`)}
                  onDuplicate={() => duplicatePage(page.id)}
                  onDelete={() => deletePage(page)}
                  onTogglePublish={() => togglePublish(page)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Page Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-[#F5F5F0] mb-6">Create New Page</h3>
            
            <div className="space-y-4">
              <div>
                <label className="form-label">Page Title</label>
                <input
                  type="text"
                  value={newPage.title}
                  onChange={(e) => {
                    setNewPage({
                      title: e.target.value,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                    });
                  }}
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="My New Page"
                />
              </div>
              
              <div>
                <label className="form-label">URL Slug</label>
                <div className="flex items-center bg-[#121212] border border-[rgba(212,175,55,0.3)]">
                  <span className="text-[#a1a1aa] px-3">/</span>
                  <input
                    type="text"
                    value={newPage.slug}
                    onChange={(e) => setNewPage({ ...newPage, slug: e.target.value })}
                    className="flex-1 bg-transparent text-[#F5F5F0] p-3 focus:outline-none"
                    placeholder="my-new-page"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createPage}
                disabled={creating}
                className="flex-1 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Page Card Component
const PageCard = ({ page, onEdit, onDuplicate, onDelete, onTogglePublish }) => {
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/${page.slug}`;
  
  return (
    <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)] transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 flex items-center justify-center rounded ${page.is_system_page ? 'bg-[#D4AF37]/20' : 'bg-[#1A1A1A]'}`}>
          {page.is_system_page ? (
            <Lock className="w-5 h-5 text-[#D4AF37]" />
          ) : (
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
          )}
        </div>
        <div>
          <h3 className="font-semibold text-[#F5F5F0] flex items-center gap-2">
            {page.title}
            {!page.is_published && (
              <span className="text-xs bg-[#52525B] text-white px-2 py-0.5 rounded">Draft</span>
            )}
          </h3>
          <p className="text-sm text-[#a1a1aa]">/{page.slug} • {page.sections?.length || 0} sections</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={onTogglePublish}
          className={`p-2 rounded transition-colors ${page.is_published ? 'text-green-500 hover:bg-green-500/10' : 'text-[#a1a1aa] hover:bg-[#1A1A1A]'}`}
          title={page.is_published ? "Unpublish" : "Publish"}
        >
          {page.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          title="View Page"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
        <button
          onClick={onDuplicate}
          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          title="Duplicate"
        >
          <Copy className="w-4 h-4" />
        </button>
        <button
          onClick={onEdit}
          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        {!page.is_system_page && (
          <button
            onClick={onDelete}
            className="p-2 text-[#A1A1AA] hover:text-red-500 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminPagesPage;
